/** Which of the Meeting View's whole-surface states to render. */
export type MeetingViewPresence =
  /** The note this view resolved to is still loading — skeleton. */
  | 'loading'
  /**
   * A `live` entry opened ahead of its recording. "New Meeting" opens the view
   * before main has begun the session — and main may hold there for a while
   * (auth hydration, a permission prompt) before the status leaves idle.
   * Nothing to show yet, but a start is still in flight.
   */
  | 'pending'
  /** There is a meeting to show: live, a saved note, or a calendar event. */
  | 'ready'
  /** Opened onto a specific note that doesn't exist (deleted, bad link). */
  | 'not-found'
  /**
   * A `live` entry with no recording to follow, now or later. Main discards a
   * recording that has no transcript (Start → Stop within seconds), so the
   * entry is left following a session that produced nothing; a start that
   * main gave up on (billing gate, permission denied) leaves it the same way.
   * There is no document for it to be about, so the view closes rather than
   * reading "not found".
   */
  | 'discarded'

export interface MeetingViewPresenceInput {
  /** How the entry was opened — see `MeetingViewContext.mode`. */
  mode: 'live' | 'note'
  variant: 'live' | 'past'
  isLoading: boolean
  hasAudioNote: boolean
  hasCalendarEvent: boolean
  /**
   * Whether this entry's recording has existed at some point: the view saw it
   * live, or the entry carries the note id the session was latched under.
   * What tells a recording that ended empty from one that hasn't begun.
   */
  sessionSeen: boolean
  /** A start is still pending — in main, or begun but not yet in this renderer's state — so a session may yet appear. */
  startPending: boolean
}

export function deriveMeetingViewPresence({
  mode,
  variant,
  isLoading,
  hasAudioNote,
  hasCalendarEvent,
  sessionSeen,
  startPending,
}: MeetingViewPresenceInput): MeetingViewPresence {
  if (isLoading) return 'loading'
  if (variant === 'past' && !hasAudioNote && !hasCalendarEvent) {
    if (mode === 'note') return 'not-found'
    return !sessionSeen && startPending ? 'pending' : 'discarded'
  }
  return 'ready'
}
